'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import type { CalculatorInputs, PricingConfig, CalculationResults } from '@/types/calculator';
import type { SupportedCurrency } from '@/lib/env';
import { formatCurrency, formatCurrencyWithDecimals } from '@/lib/calculator-utils';
import { getAccessNowTotalCost, getROIPercentage, getNetAnnualSavings, getROIPaybackTimeline, hasPositiveNetAnnualSavings } from '@/lib/pricing-helpers';
import { formatHourlyRate } from '@/lib/calculator-utils';
import { CALCULATION_CONSTANTS } from '@/lib/env';

interface CalculatorDebugModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inputs: CalculatorInputs;
  pricing?: PricingConfig;
  results: CalculationResults | null;
  currency: SupportedCurrency;
}

export function CalculatorDebugModal({
  open,
  onOpenChange,
  inputs,
  pricing,
  results,
  currency
}: CalculatorDebugModalProps) {
  const formatValue = (value: unknown, key?: string): string => {
    if (typeof value === 'number') {
      // Don't format these fields as currency - they're counts/percentages/time values
      const nonCurrencyFields = [
        'cardholderDatabaseSize', 'cardReplacementPercentage', 'staffTurnoverRate', 'staffGrowthRate',
        'accessRequestsPerCardholder', 'credentialRequestsPerMonth', 'accessRequestsPerMonth',
        'visitorsPerMonth', 'timePerRequest', 'timePerVisitor', 'accessNowTimePerRequest', 
        'accessNowTimePerVisitor', 'cardholderAdmins', 'extraRequestCapacityPercentage'
      ];
      // operationalInsuranceValue and costPerEmployee should be formatted as currency
      
      if (key && nonCurrencyFields.includes(key)) {
        return value.toString();
      }
      
      // Check if it looks like a currency value (> 100)
      if (value > 100 && (value % 1 === 0 || value.toString().includes('.'))) {
        return formatCurrency(value, currency);
      }
      return value.toString();
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  const renderObject = (obj: Record<string, unknown>, title: string) => (
    <div className="mb-6">
      <h3 className="font-semibold text-foreground mb-2">{title}</h3>
      <div className="bg-muted/30 p-3 rounded text-sm font-mono">
        {Object.entries(obj).map(([key, value]) => (
          <div key={key} className="mb-1">
            <span className="text-muted-foreground">{key}:</span>{' '}
            <span className="text-foreground">{formatValue(value, key)}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-full max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Calculator Debug Information</DialogTitle>
          <DialogClose onClose={() => onOpenChange(false)} />
        </DialogHeader>
        
        <div className="p-6 space-y-6 overflow-auto">
          <div className="text-sm text-muted-foreground mb-4">
            Currency: <span className="font-semibold text-foreground">{currency}</span>
          </div>

          {/* Inputs */}
          {renderObject(inputs as unknown as Record<string, unknown>, 'Calculator Inputs')}

          {/* Pricing Configuration */}
          {pricing && renderObject(pricing as unknown as Record<string, unknown>, 'Pricing Configuration')}

          {/* Results */}
          {results ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Calculation Results</h3>
              
              {/* Summary Metrics */}
              <div className="bg-muted/30 p-3 rounded text-sm">
                <h4 className="font-medium mb-2">Summary</h4>
                <div className="font-mono space-y-1">
                  <div>Manual Labor Cost: <span className="text-red-600 font-semibold">{formatCurrency(results.totalManualCost, currency)}/year</span></div>
                  <div>AccessNow Annual Cost: <span className="text-blue-600 font-semibold">{formatCurrency(getAccessNowTotalCost(results), currency)}/year</span></div>
                  <div>{hasPositiveNetAnnualSavings(results) ? 'Net Annual Savings' : 'Annual Investment Cost'}: <span className={`font-semibold ${hasPositiveNetAnnualSavings(results) ? 'text-green-600' : 'text-blue-600'}`}>{hasPositiveNetAnnualSavings(results) ? '+' : ''}{formatCurrency(Math.abs(getNetAnnualSavings(results)), currency)}/year</span></div>
                  <div>FTE Reduction: <span className="font-semibold">{results.fteReduction.toFixed(2)}</span></div>
                  {getROIPercentage(results) !== null && (
                    <div>Annual ROI: <span className="font-semibold text-green-600">{getROIPercentage(results)}%</span></div>
                  )}
                  <div>Business Value Returned: <span className="text-blue-600 font-semibold">{formatCurrency(results.businessEfficiencyValue, currency)}/year</span></div>
                  {getROIPaybackTimeline(results).hasPositiveSavings && (
                    <div>Payback Period: <span className="font-semibold text-green-600">{getROIPaybackTimeline(results).monthsToPayback} months</span></div>
                  )}
                </div>
              </div>

              {/* Workflow Breakdown */}
              <div className="bg-muted/30 p-3 rounded text-sm">
                <h4 className="font-medium mb-2">Workflow Breakdown</h4>
                <div className="text-xs text-muted-foreground mb-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded">
                  <strong>Note:</strong> All calculations below use base request counts (without capacity buffer) for operational cost analysis. The capacity buffer is only applied for AccessNow licensing calculations.
                </div>
                <div className="space-y-4">
                  {(() => {
                    // Calculate workflow-specific values from base requests
                    const credentialRequests = inputs.credentialRequestsPerMonth > 0
                      ? inputs.credentialRequestsPerMonth * 12
                      : Math.round((inputs.cardholderDatabaseSize * inputs.cardReplacementPercentage) / 100) + 
                        Math.round((inputs.cardholderDatabaseSize * inputs.staffTurnoverRate) / 100) +
                        Math.round((inputs.cardholderDatabaseSize * inputs.staffGrowthRate) / 100);
                    const accessRequests = inputs.cardholderDatabaseSize > 0
                      ? inputs.cardholderDatabaseSize * inputs.accessRequestsPerCardholder
                      : inputs.accessRequestsPerMonth * 12;
                    const visitorRequests = inputs.hasVisitorRegistration ? inputs.visitorsPerMonth * 12 : 0;
                    
                    // Calculate hours for each workflow
                    const credentialManualHours = (credentialRequests * inputs.timePerRequest) / 60;
                    const accessManualHours = (accessRequests * inputs.timePerRequest) / 60;
                    const visitorManualHours = (visitorRequests * inputs.timePerVisitor) / 60;
                    
                    const credentialAccessNowHours = (credentialRequests * inputs.accessNowTimePerRequest) / 60;
                    const accessAccessNowHours = (accessRequests * inputs.accessNowTimePerRequest) / 60;
                    const visitorAccessNowHours = (visitorRequests * inputs.accessNowTimePerVisitor) / 60;
                    
                    // Calculate costs
                    const hourlyRate = inputs.costPerEmployee / 2080; // Annual hours
                    const credentialManualCost = credentialManualHours * hourlyRate;
                    const accessManualCost = accessManualHours * hourlyRate;
                    const visitorManualCost = visitorManualHours * hourlyRate;
                    
                    const credentialAccessNowCost = credentialAccessNowHours * hourlyRate;
                    const accessAccessNowCost = accessAccessNowHours * hourlyRate;
                    const visitorAccessNowCost = visitorAccessNowHours * hourlyRate;
                    
                    const sections = [];
                    
                    if (credentialRequests > 0) {
                      sections.push(
                        <div key="credential" className="grid md:grid-cols-2 gap-3">
                          <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded">
                            <div className="font-medium text-red-700 dark:text-red-300 mb-2">Credential Management - Manual</div>
                            <div className="space-y-1 text-xs">
                              <div>Requests: {credentialRequests.toLocaleString()}</div>
                              <div>Hours: {Math.round(credentialManualHours).toLocaleString()}</div>
                              <div>Cost: {formatCurrency(credentialManualCost, currency)}</div>
                              <div>Cost per Request: {formatCurrencyWithDecimals(credentialManualCost / credentialRequests, currency)}</div>
                            </div>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded">
                            <div className="font-medium text-blue-700 dark:text-blue-300 mb-2">Credential Management - AccessNow</div>
                            <div className="space-y-1 text-xs">
                              <div>Requests: {credentialRequests.toLocaleString()}</div>
                              <div>Hours: {Math.round(credentialAccessNowHours).toLocaleString()}</div>
                              <div>Labor Cost: {formatCurrency(credentialAccessNowCost, currency)}</div>
                              <div>Labor Cost per Request: {formatCurrencyWithDecimals(credentialAccessNowCost / credentialRequests, currency)}</div>
                              <div>Hours Saved: {Math.round(credentialManualHours - credentialAccessNowHours).toLocaleString()}</div>
                              <div>Savings per Request: {formatCurrencyWithDecimals((credentialManualCost - credentialAccessNowCost) / credentialRequests, currency)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    if (accessRequests > 0) {
                      sections.push(
                        <div key="access" className="grid md:grid-cols-2 gap-3">
                          <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded">
                            <div className="font-medium text-red-700 dark:text-red-300 mb-2">Access Management - Manual</div>
                            <div className="space-y-1 text-xs">
                              <div>Requests: {accessRequests.toLocaleString()}</div>
                              <div>Hours: {Math.round(accessManualHours).toLocaleString()}</div>
                              <div>Cost: {formatCurrency(accessManualCost, currency)}</div>
                              <div>Cost per Request: {formatCurrencyWithDecimals(accessManualCost / accessRequests, currency)}</div>
                            </div>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded">
                            <div className="font-medium text-blue-700 dark:text-blue-300 mb-2">Access Management - AccessNow</div>
                            <div className="space-y-1 text-xs">
                              <div>Requests: {accessRequests.toLocaleString()}</div>
                              <div>Hours: {Math.round(accessAccessNowHours).toLocaleString()}</div>
                              <div>Labor Cost: {formatCurrency(accessAccessNowCost, currency)}</div>
                              <div>Labor Cost per Request: {formatCurrencyWithDecimals(accessAccessNowCost / accessRequests, currency)}</div>
                              <div>Hours Saved: {Math.round(accessManualHours - accessAccessNowHours).toLocaleString()}</div>
                              <div>Savings per Request: {formatCurrencyWithDecimals((accessManualCost - accessAccessNowCost) / accessRequests, currency)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    if (visitorRequests > 0) {
                      sections.push(
                        <div key="visitor" className="grid md:grid-cols-2 gap-3">
                          <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded">
                            <div className="font-medium text-red-700 dark:text-red-300 mb-2">Visitor Management - Manual</div>
                            <div className="space-y-1 text-xs">
                              <div>Visitors: {visitorRequests.toLocaleString()}</div>
                              <div>Hours: {Math.round(visitorManualHours).toLocaleString()}</div>
                              <div>Cost: {formatCurrency(visitorManualCost, currency)}</div>
                              <div>Cost per Visitor: {formatCurrencyWithDecimals(visitorManualCost / visitorRequests, currency)}</div>
                            </div>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded">
                            <div className="font-medium text-blue-700 dark:text-blue-300 mb-2">Visitor Management - AccessNow</div>
                            <div className="space-y-1 text-xs">
                              <div>Visitors: {visitorRequests.toLocaleString()}</div>
                              <div>Hours: {Math.round(visitorAccessNowHours).toLocaleString()}</div>
                              <div>Labor Cost: {formatCurrency(visitorAccessNowCost, currency)}</div>
                              <div>Labor Cost per Visitor: {formatCurrencyWithDecimals(visitorAccessNowCost / visitorRequests, currency)}</div>
                              <div>Hours Saved: {Math.round(visitorManualHours - visitorAccessNowHours).toLocaleString()}</div>
                              <div>Savings per Visitor: {formatCurrencyWithDecimals((visitorManualCost - visitorAccessNowCost) / visitorRequests, currency)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return sections;
                  })()}
                </div>
              </div>

              {/* Cost Calculations */}
              <div className="bg-muted/30 p-3 rounded text-sm">
                <h4 className="font-medium mb-2">Cost Calculations</h4>
                <div className="text-xs text-muted-foreground mb-3 p-2 bg-green-50 dark:bg-green-950/30 rounded">
                  <strong>Reference:</strong> These totals match the &quot;Labour Hours&quot; shown in the ROI Summary cards above. Manual hours and AccessNow hours include all workflows (credential + access + visitor).
                </div>
                <div className="font-mono space-y-2">
                  <div className="pb-1 border-b border-muted">
                    <div className="text-muted-foreground text-xs mb-1">Hourly Rate Calculation</div>
                    <div>Employee Hourly Rate: <span className="font-semibold">{formatHourlyRate(inputs.costPerEmployee, currency, CALCULATION_CONSTANTS.HOURS_PER_YEAR)}</span></div>
                    <div className="text-xs text-muted-foreground">({formatCurrency(inputs.costPerEmployee, currency)} ÷ {CALCULATION_CONSTANTS.HOURS_PER_YEAR.toLocaleString()} hours/year)</div>
                  </div>
                  
                  <div className="pb-1 border-b border-muted">
                    <div className="text-muted-foreground text-xs mb-1">Manual Process Costs (Current)</div>
                    <div>Total Annual Requests: <span className="font-semibold">{results.baseRequestsPerYear?.toLocaleString() || '0'}</span></div>
                    <div>Total Manual Hours: <span className="font-semibold">{results.totalManualHours?.toLocaleString() || '0'}</span></div>
                    <div>Cost per Manual Request: <span className="text-red-600 font-semibold">{formatCurrencyWithDecimals((inputs.costPerEmployee / CALCULATION_CONSTANTS.HOURS_PER_YEAR) * (inputs.timePerRequest / 60), currency)}</span></div>
                    <div>Total Manual Cost: <span className="text-red-600 font-semibold">{formatCurrency(results.totalManualCost, currency)}</span></div>
                  </div>
                  
                  <div className="pb-1 border-b border-muted">
                    <div className="text-muted-foreground text-xs mb-1">AccessNow Process Costs (After)</div>
                    <div>Avg. AccessNow Time: <span className="font-semibold">{results.baseRequestsPerYear ? ((inputs.accessNowTimePerRequest * (results.baseRequestsPerYear - (inputs.hasVisitorRegistration ? inputs.visitorsPerMonth * 12 : 0)) + inputs.accessNowTimePerVisitor * (inputs.hasVisitorRegistration ? inputs.visitorsPerMonth * 12 : 0)) / results.baseRequestsPerYear).toFixed(1) : '0'} min/request</span></div>
                    <div>Total AccessNow Hours: <span className="font-semibold">{results.totalAccessNowHours?.toLocaleString() || '0'}</span></div>
                    <div>Labor Cost (After): <span className="text-blue-600 font-semibold">{formatCurrency(results.totalManualCost - results.businessEfficiencyValue, currency)}</span></div>
                    <div>AccessNow License Cost: <span className="text-blue-600 font-semibold">{formatCurrency(getAccessNowTotalCost(results), currency)}</span></div>
                    <div>Total Cost (After): <span className="text-blue-600 font-semibold">{formatCurrency((results.totalManualCost - results.businessEfficiencyValue) + getAccessNowTotalCost(results), currency)}</span></div>
                  </div>
                  
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Annual Impact</div>
                    <div>Time Savings: <span className="text-green-600 font-semibold">{results.totalHoursSaved.toLocaleString()} hours/year</span></div>
                    <div>{hasPositiveNetAnnualSavings(results) ? 'Net Annual Savings' : 'Annual Investment Cost'}: <span className={`font-semibold ${hasPositiveNetAnnualSavings(results) ? 'text-green-600' : 'text-blue-600'}`}>{hasPositiveNetAnnualSavings(results) ? '+' : ''}{formatCurrency(Math.abs(getNetAnnualSavings(results)), currency)}/year</span></div>
                    <div>Efficiency Gain: <span className="text-green-600 font-semibold">{((results.businessEfficiencyValue / results.totalManualCost) * 100).toFixed(1)}%</span></div>
                    {getROIPercentage(results) !== null && (
                      <div>Annual ROI: <span className="font-semibold text-green-600">{getROIPercentage(results)}%</span></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Workflow Breakdown */}
              <div className="bg-muted/30 p-3 rounded text-sm">
                <h4 className="font-medium mb-2">Workflow Savings</h4>
                <div className="font-mono space-y-1">
                  <div>Credential Workflow: {formatCurrency(results.credentialWorkflowSavings, currency)}</div>
                  <div>Access Request Workflow: {formatCurrency(results.accessWorkflowSavings, currency)}</div>
                  {results.visitorWorkflowSavings > 0 && (
                    <div>Visitor Workflow: {formatCurrency(results.visitorWorkflowSavings, currency)}</div>
                  )}
                </div>
              </div>

              {/* AccessNow Pricing Breakdown */}
              <div className="bg-muted/30 p-3 rounded text-sm">
                <h4 className="font-medium mb-2">AccessNow Pricing</h4>
                <div className="font-mono space-y-1">
                  <div>Workflows: {results.accessNowPricing.workflows} × {formatCurrency(pricing?.workflowPrice || 0, currency)}</div>
                  <div>Small Request Packages: {results.accessNowPricing.extraRequestsSmall} × {formatCurrency(pricing?.extraRequestsSmallPrice || 0, currency)}</div>
                  <div>Large Request Packages: {results.accessNowPricing.extraRequestsLarge} × {formatCurrency(pricing?.extraRequestsLargePrice || 0, currency)}</div>
                  <div className="border-t pt-1 mt-1 font-semibold">
                    Total: {formatCurrency(getAccessNowTotalCost(results), currency)}
                  </div>
                </div>
              </div>

              {/* Request Calculations */}
              <div className="bg-muted/30 p-3 rounded text-sm">
                <h4 className="font-medium mb-2">Request Calculations</h4>
                <div className="font-mono space-y-1">
                  <div className="pb-1 border-b border-muted">
                    <div className="text-muted-foreground text-xs mb-1">Base Request Calculations</div>
                    <div>- Credential Requests: {
                      inputs.credentialRequestsPerMonth > 0
                        ? `${(inputs.credentialRequestsPerMonth * 12).toLocaleString()} (Simple: ${inputs.credentialRequestsPerMonth}/month × 12)`
                        : `${(Math.round((inputs.cardholderDatabaseSize * inputs.cardReplacementPercentage) / 100) + Math.round((inputs.cardholderDatabaseSize * inputs.staffTurnoverRate) / 100) + Math.round((inputs.cardholderDatabaseSize * inputs.staffGrowthRate) / 100)).toLocaleString()}`
                    }</div>
                    {inputs.credentialRequestsPerMonth === 0 && inputs.cardholderDatabaseSize > 0 && (
                      <div className="text-xs text-muted-foreground ml-2">
                        = {Math.round((inputs.cardholderDatabaseSize * inputs.cardReplacementPercentage) / 100)} replacements + {Math.round((inputs.cardholderDatabaseSize * inputs.staffTurnoverRate) / 100)} turnover + {Math.round((inputs.cardholderDatabaseSize * inputs.staffGrowthRate) / 100)} growth
                      </div>
                    )}
                    <div>- Access Requests: {
                      inputs.cardholderDatabaseSize > 0 
                        ? `${(inputs.cardholderDatabaseSize * inputs.accessRequestsPerCardholder).toLocaleString()} (Database: ${inputs.cardholderDatabaseSize} × ${inputs.accessRequestsPerCardholder})`
                        : `${(inputs.accessRequestsPerMonth * 12).toLocaleString()} (Simple: ${inputs.accessRequestsPerMonth}/month × 12)`
                    }</div>
                    {inputs.hasVisitorRegistration && (
                      <div>- Visitor Requests: {(inputs.visitorsPerMonth * 12).toLocaleString()} ({inputs.visitorsPerMonth}/month × 12)</div>
                    )}
                    <div className="pt-1 border-t border-muted font-medium">
                      Base Total: {
                        (() => {
                          const credentialRequests = inputs.credentialRequestsPerMonth > 0
                            ? inputs.credentialRequestsPerMonth * 12
                            : Math.round((inputs.cardholderDatabaseSize * inputs.cardReplacementPercentage) / 100) + 
                              Math.round((inputs.cardholderDatabaseSize * inputs.staffTurnoverRate) / 100) +
                              Math.round((inputs.cardholderDatabaseSize * inputs.staffGrowthRate) / 100);
                          const accessRequests = inputs.cardholderDatabaseSize > 0
                            ? inputs.cardholderDatabaseSize * inputs.accessRequestsPerCardholder
                            : inputs.accessRequestsPerMonth * 12;
                          const visitorRequests = inputs.hasVisitorRegistration ? inputs.visitorsPerMonth * 12 : 0;
                          return (credentialRequests + accessRequests + visitorRequests).toLocaleString();
                        })()
                      }
                    </div>
                  </div>
                  
                  {inputs.extraRequestCapacityPercentage > 0 && (
                    <div className="pb-1 border-b border-muted">
                      <div className="text-muted-foreground text-xs mb-1">Capacity Buffer Applied</div>
                      <div>Extra Capacity: +{inputs.extraRequestCapacityPercentage}%</div>
                      <div>Capacity Buffer: {
                        (() => {
                          const credentialRequests = inputs.credentialRequestsPerMonth > 0
                            ? inputs.credentialRequestsPerMonth * 12
                            : Math.round((inputs.cardholderDatabaseSize * inputs.cardReplacementPercentage) / 100) + 
                              Math.round((inputs.cardholderDatabaseSize * inputs.staffTurnoverRate) / 100) +
                              Math.round((inputs.cardholderDatabaseSize * inputs.staffGrowthRate) / 100);
                          const accessRequests = inputs.cardholderDatabaseSize > 0
                            ? inputs.cardholderDatabaseSize * inputs.accessRequestsPerCardholder
                            : inputs.accessRequestsPerMonth * 12;
                          const visitorRequests = inputs.hasVisitorRegistration ? inputs.visitorsPerMonth * 12 : 0;
                          const baseTotal = credentialRequests + accessRequests + visitorRequests;
                          const capacityBuffer = Math.round(baseTotal * (inputs.extraRequestCapacityPercentage / 100));
                          return capacityBuffer.toLocaleString();
                        })()
                      } requests</div>
                    </div>
                  )}
                  
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Final Totals</div>
                    <div className="font-semibold">Base Requests (Operations): {results.baseRequestsPerYear?.toLocaleString() || '0'}</div>
                    <div className="font-semibold">Licensed Requests (with buffer): {results.totalRequestsPerYear.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground text-center py-8">
              No calculation results available. Run a calculation to see results.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
